import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useFirstVisitMode } from "@/lib/firstVisit";

/**
 * Single, app-wide Calendly badge mount point. Lives at the App level so
 * React Router transitions don't unmount/remount it across the allowed
 * pages — which was causing the in/out race that wiped the badge when
 * Landing → Dashboard → Navigator was navigated quickly.
 *
 * Logic:
 *   • If pathname is in ALLOWED_PATHS, ensure exactly one badge exists.
 *   • Else, remove any badge that's hanging around from a previous route.
 *
 * Pure side-effect component — renders no JSX.
 */
const CSS_HREF = "https://assets.calendly.com/assets/external/widget.css";
const JS_SRC = "https://assets.calendly.com/assets/external/widget.js";

const ALLOWED_PATHS = new Set(["/navigator"]);

const BADGE_CONFIG = {
    url: "https://calendly.com/ashlee-asvmedicalservices",
    text: "Talk to a dementia NP",
    color: "#5cbf98",
    textColor: "#ffffff",
    branding: true,
};

function ensureAssetsLoaded() {
    if (typeof document === "undefined") return Promise.resolve();
    if (!document.querySelector(`link[href="${CSS_HREF}"]`)) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = CSS_HREF;
        document.head.appendChild(link);
    }
    return new Promise((resolve) => {
        if (window.Calendly) { resolve(); return; }
        let script = document.querySelector(`script[data-calendly-loader="true"]`);
        if (!script) {
            script = document.createElement("script");
            script.src = JS_SRC;
            script.async = true;
            script.dataset.calendlyLoader = "true";
            document.body.appendChild(script);
        }
        const check = () => (window.Calendly ? resolve() : setTimeout(check, 50));
        check();
    });
}

function removeBadge() {
    if (typeof document === "undefined") return;
    document.querySelectorAll(".calendly-badge-widget, .calendly-overlay").forEach((el) => {
        try { el.remove(); } catch (_e) { /* ignore */ }
    });
}

function ensureBadge() {
    if (typeof document === "undefined" || !window.Calendly?.initBadgeWidget) return;
    // If the pill is already on the page, leave it alone. We never want
    // back-to-back initBadgeWidget calls (Calendly creates duplicates).
    if (document.querySelector(".calendly-badge-widget")) return;
    try {
        window.Calendly.initBadgeWidget(BADGE_CONFIG);
    } catch (_e) { /* swallow — widget.js will retry */ }
}

export default function GlobalCalendlyBadge() {
    const { pathname } = useLocation();
    const firstVisit = useFirstVisitMode();
    // Phones get the fixed bottom nav — Calendly's bottom-right pill would sit
    // on top of it, so the badge is desktop/tablet-only. Reactive: rotating a
    // phone to landscape and back removes it again instead of leaving it stuck.
    const [isDesktop, setIsDesktop] = useState(
        () => typeof window === "undefined" || window.matchMedia("(min-width: 768px)").matches
    );
    useEffect(() => {
        const mq = window.matchMedia("(min-width: 768px)");
        const onChange = (e) => setIsDesktop(e.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);
    const allowed = isDesktop && !firstVisit && ALLOWED_PATHS.has(pathname);

    useEffect(() => {
        let cancelled = false;
        if (allowed) {
            ensureAssetsLoaded().then(() => {
                if (!cancelled) ensureBadge();
            });
        } else {
            removeBadge();
        }
        return () => { cancelled = true; };
    }, [allowed]);

    return null;
}
